//
// Copyright (c) 2011-2017 by Curalate, Inc.
//

import moment from "moment-timezone";

(function() {
  const curalate = angular.module('curalate');

    /**
     * ngMinIf helps validate numeric input values under certain conditions
     *
     * Angular does not support condition validation of min/max values in the way they support it for "required" fields
     * This directive allows the caller to specify a minimum value to validate the form against as well as a condition to
     * determine whether to check the value.
     *
     * Usage:
     *  <input type="number" ng-min-if="{'min': 5, 'condition': model.checkMinCondition } />
     */
  curalate.directive('ngMinIf',
        () => ({
          restrict: 'A',
          require: 'ngModel',
          link(scope, element, attrs, ngModel) {
            let condition;
            let num;
            let validateFunc;

            scope.$watch(() => {
              const obj = scope.$eval(attrs.ngMinIf);
              const prevCondition = condition;

              num = parseInt(obj.value, 10);
              condition = obj.condition;

              if (prevCondition !== condition) {
                validateFunc();
              }
            });

            validateFunc = function(modelValue) {
              if (condition) {
                return !(isNaN(modelValue) || modelValue < num);
              }
              return true;
            };

            ngModel.$validators.min = validateFunc;

            validateFunc(ngModel.$modelValue);
          },
        }),
    );

    /**
     * formatDateTime acts like a filter on an input with an ngModel
     *
     * If the ngModel is using a Date object (or something parseable by moment.js), the caller can specify a
     * date format using this directive to output the model in a certain way
     *
     * Usage:
     *   <input ng-model="model.date" format-date-time="MM/DD/YYYY">
     */
  curalate.directive('formatDateTime', () => ({
    restrict: 'A',
    require: 'ngModel',
    link(scope, element, attrs, ngModel) {
      ngModel.$parsers.unshift(value => moment(value, attrs.formatDateTime).toDate());

      ngModel.$formatters.unshift(value => moment(value).format(attrs.formatDateTime));
    },
  }));

    /**
     * fileRequired provides form validation for input[type=file], because Angular does not provide this
     *
     * If the ngRequired evaluates to true, the directive will validate that the element has at least one file
     */
  curalate.directive('fileRequired',
        () => ({
          restrict: 'A',
          require: '?ngModel',
          link(scope, element, attrs, ngModel) {
            const validateFunc = function() {
              if (!scope.$eval(attrs.fileRequired)) {
                ngModel.$setValidity('hasFile', true);
                return;
              }

              ngModel.$setValidity('hasFile', ngModel.$modelValue instanceof Blob || element[0].files.length > 0);
            };

            if (ngModel) {
              element.on('change.fileRequired', () => {
                if (scope.$eval(attrs.fileRequired)) {
                  scope.$apply(() => {
                    validateFunc();
                  });
                }
              });

              scope.$on('$destroy', () => {
                element.off('change.fileRequired');
              });

              ngModel.$formatters.push(validateFunc);
              ngModel.$parsers.push(validateFunc);

              validateFunc();
            }
          },
        }),
    );

    /**
     * Adds a parser function to the ngModel's $parser pipeline which appends "http://" if the string is missing from the input
     */
  curalate.directive('addProtocolIfMissing',
        () => {
          const HTTP_REGEX = /^https?:\/\//;

          return {
            restrict: 'A',
            require: 'ngModel',
            link(scope, element, attrs, ngModel) {
              const addProtocol = function(value) {
                if (!HTTP_REGEX.test(value)) {
                  return `http://${value}`;
                }
                return value;
              };

              ngModel.$parsers.unshift(addProtocol);
            },
          };
        },
    );
})();
